import React from "react";
import PropTypes from "prop-types";
import { Empty, Icon, Input } from "antd";
import CardBlock from "./card-block";
import { formatCompactAmount, formatLakhAmount } from "../money-format";
import { formatCurrency } from "utilities/currency";
import LimitTrackerModal from "./limit-tracker-modal";

// Total Exposure breakdown rows, in display order. Keys match exposure_data's
// shape (see the case-detail API's `exposure_data` object).
const EXPOSURE_BREAKDOWN_ROWS = [
    { key: "proposed_limit", label: "Proposed Limit" },
    { key: "existing_exposure", label: "Existing Exposure" },
    { key: "group_exposure", label: "Group Exposure" }
];

// The two tiles backed by the generic `case/<id>/limit/` endpoint (see
// CaseService.updateLimit / getLimitHistory), each opening the shared
// LimitTrackerModal keyed by its own limit_type.
const LIMIT_META = {
    sales_proposed_limit: {
        limitType: "SALES_PROPOSED_LIMIT",
        title: "Sales Proposed Limit Tracker",
        fieldLabel: "Sales Proposed Limit"
    },
    proposed_limit: {
        limitType: "PROPOSED_LIMIT",
        title: "Proposed Limit Tracker",
        fieldLabel: "Proposed Limit"
    }
};

/**
 * Limit Assessment — the headline metrics (last-N-months sales, monthly sales,
 * limit-as-per-sales, proposed limits) with a compact/full amount toggle, and
 * the Sales Proposed Limit / Proposed Limit tiles behind their own tracker
 * modal (editable value + mandatory remark + audit history).
 *
 * Extracted from case-summary-section so the SAME card can be rendered in more
 * than one place (its own Limit Assessment section, and inside the Credit TVR's
 * Financial Performance subsection) without a second copy of the logic.
 *
 * `title` defaults to "Limit Assessment"; pass null where the host already
 * provides that heading (the standalone section does), so it is not doubled.
 *
 * `variant` picks the rendering:
 *   - "cards" (default): the interactive metric tiles with the compact/full
 *     toggle and the Sales Proposed Limit / Proposed Limit trackers — used by
 *     the standalone Limit Assessment section.
 *   - "tvr": every block is rendered as a disabled text field, laid out on the
 *     same grid as the surrounding Credit-TVR form so it reads as part of it.
 *     Nothing is editable here — the figures come from the same source the
 *     standalone card owns, so the TVR just displays them.
 */
class LimitAssessmentCard extends React.Component {
    // `trackerMetric` names WHICH limit tile's tracker modal is open (Sales
    // Proposed Limit or Proposed Limit) — at most one at a time.
    state = { compact: true, trackerMetric: null };

    setCompact = compact => this.setState({ compact });

    // Proposed Limit's original permission gate (unchanged).
    canEdit() {
        const {
            isRiskApprover,
            isUserAllowedToEdit,
            recreditViewOnlyPermission
        } = this.props;
        return (
            !isRiskApprover && isUserAllowedToEdit && !recreditViewOnlyPermission
        );
    }

    // Per-tile edit gate. Sales Proposed Limit is editable purely at the credit
    // stages (7, 8) — no other checks — while Proposed Limit keeps its original
    // permission gate.
    canEditMetric(metric) {
        if (metric && metric.key === "sales_proposed_limit") {
            return [7, 8].includes(Number(this.props.stageId));
        }
        return this.canEdit();
    }

    // Resolves an editable tile's live value + write path from its key. Returns
    // null for non-editable tiles (or ones with no handler wired).
    editableFor(metric) {
        if (!metric || !metric.editable || !metric.key) {
            return null;
        }
        if (metric.key === "sales_proposed_limit") {
            return this.props.onSalesProposedLimitChange
                ? {
                    value: this.props.salesProposedLimit,
                    onChange: this.props.onSalesProposedLimitChange
                }
                : null;
        }
        // Default / "proposed_limit".
        return this.props.onProposedLimitChange
            ? {
                value: this.props.proposedLimit,
                onChange: this.props.onProposedLimitChange
            }
            : null;
    }

    // Opened from a limit tile's edit (allowed) or view (not allowed) icon,
    // regardless of whether a live value/handler is wired, so history stays
    // reachable even when the tile can't be edited right now.
    openTracker = metric => this.setState({ trackerMetric: metric });

    closeTracker = () => this.setState({ trackerMetric: null });

    submitTrackedLimit = (value, remark) => {
        const ec = this.editableFor(this.state.trackerMetric);
        return ec.onChange(value, remark);
    };

    renderToggle() {
        const { compact } = this.state;
        const options = [
            { key: true, label: "Compact" },
            { key: false, label: "Full value" }
        ];
        return (
            <div className={"cc-unit-toggle"} role={"group"}>
                {options.map(opt => (
                    <button
                        key={String(opt.key)}
                        type={"button"}
                        className={
                            "cc-unit-toggle__opt" +
                            (compact === opt.key ? " is-active" : "")
                        }
                        aria-pressed={compact === opt.key}
                        onClick={() => this.setCompact(opt.key)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>
        );
    }

    metricDisplay(metric) {
        if (this.state.compact) {
            // `lakh` rows are pinned to lakhs so the rows of one card stay on a
            // single scale and can be compared by eye.
            const compact = metric.lakh
                ? formatLakhAmount(metric.amount)
                : formatCompactAmount(metric.amount);
            if (compact !== null) {
                // Limit Assessment abbreviates the unit: "12.5 Lakh" -> "12.5 L".
                // Scoped here (not in the shared formatter, which credit-decision
                // also uses) so only this section shows "L".
                return compact.replace(/\bLakh\b/, "L");
            }
        }
        return metric.value;
    }

    // Sales Proposed Limit / Proposed Limit — neither edits inline. Their icon
    // (edit when canEditMetric allows it, view otherwise) opens the shared
    // tracker modal instead.
    renderLimitTileValue(metric) {
        // Prefer the live value so the tile reflects a just-saved edit
        // immediately (topMetrics is rebuilt from a different slice).
        const ec = this.editableFor(metric);
        const live = ec && ec.value !== null && ec.value !== undefined;
        const shown = live
            ? {
                ...metric,
                amount: ec.value,
                value: formatCurrency(ec.value, true)
            }
            : metric;
        const hasValue =
            shown.value !== null && shown.value !== undefined && shown.value !== "";
        const display = hasValue ? this.metricDisplay(shown) : "—";
        return (
            <span className={"cc-metric__value"}>
                <span className={"cc-metric__unit"}>₹</span>
                {display}
                <Icon
                    type={this.canEditMetric(metric) ? "edit" : "eye"}
                    className={"cc-metric__edit"}
                    onClick={() => this.openTracker(metric)}
                    style={{
                        marginLeft: 8,
                        cursor: "pointer",
                        fontSize: 14
                    }}
                />
                {metric.extraLine ? (
                    <div className={"cc-metric__extra"}>{metric.extraLine}</div>
                ) : null}
            </span>
        );
    }

    renderMetricValue(metric) {
        if (LIMIT_META[metric.key]) {
            return this.renderLimitTileValue(metric);
        }
        // Tiles flagged `na` read "N.A." — no ₹ — whatever the underlying
        // number is. Nothing currently sets this; kept as a general escape
        // hatch for a future non-applicable tile.
        if (metric.na) {
            return <span className={"cc-metric__value"}>N.A.</span>;
        }
        // `alwaysShow` tiles survive the upstream filter with no value at all
        // (e.g. Sales never filed an ask) — render a dash, not a stray "₹".
        const hasValue =
            metric.value !== null &&
            metric.value !== undefined &&
            metric.value !== "";
        if (!hasValue) {
            return <span className={"cc-metric__value"}>—</span>;
        }
        return (
            <span className={"cc-metric__value"}>
                <span className={"cc-metric__unit"}>₹</span>
                {this.metricDisplay(metric)}
                {metric.extraLine ? (
                    <div className={"cc-metric__extra"}>{metric.extraLine}</div>
                ) : null}
            </span>
        );
    }

    // Multi-row metric card (e.g., GST Sales YTD / FY26 / FY25) — mirrors the
    // stacked layout the PDF one-pager uses for the Limit Assessment tile.
    renderRowedMetric(metric) {
        return (
            <div className={"cc-metric cc-metric--rows"} key={metric.label}>
                <span className={"cc-metric__label"}>{metric.label}</span>
                <div className={"cc-metric__rows"}>
                    {metric.rows.map(row => {
                        const rawAmount = row.amount;
                        const isZero =
                            rawAmount !== null &&
                            rawAmount !== undefined &&
                            rawAmount !== "" &&
                            Number(rawAmount) === 0;
                        return (
                            <div className={"cc-metric__row"} key={row.label}>
                                <span className={"cc-metric__row-label"}>
                                    {row.label}
                                </span>
                                <span className={"cc-metric__row-value"}>
                                    {isZero ? (
                                        "N.A."
                                    ) : (
                                        <React.Fragment>
                                            <span
                                                className={"cc-metric__unit"}
                                            >
                                                ₹
                                            </span>
                                            {this.metricDisplay(row)}
                                        </React.Fragment>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Total Exposure — one more tile in the SAME `.cc-metrics` grid as GST
    // Sales / Ledger Sales / the limit tiles (same cc-metric/cc-metric--rows
    // classes, so it matches their height/width exactly), just with a refresh
    // icon next to its label. Only rendered when the host wires a refresh
    // handler (today just the standalone Limit Assessment section).
    renderExposureMetric() {
        const { exposureData, exposureRefreshing, onRefreshExposure } = this.props;
        if (!onRefreshExposure) {
            return null;
        }
        // Same lakh scale + compact/full toggle every other Limit Assessment
        // amount uses (this.metricDisplay reads state.compact). Total Exposure
        // is flagged `highlight` so it keeps full contrast while the breakdown
        // rows underneath are muted — the total should read first.
        const rows = [
            {
                key: "total_exposure",
                label: "Total Exposure",
                amount: exposureData && exposureData.total_exposure,
                value: formatCurrency(
                    exposureData && exposureData.total_exposure,
                    true
                ),
                lakh: true,
                highlight: true
            },
            ...EXPOSURE_BREAKDOWN_ROWS.map(row => ({
                key: row.key,
                label: row.label,
                amount: exposureData && exposureData[row.key],
                value: formatCurrency(
                    exposureData && exposureData[row.key],
                    true
                ),
                lakh: true
            }))
        ];
        return (
            <div className={"cc-metric cc-metric--rows"} key={"total_exposure"}>
                <span className={"cc-metric__label"}>
                    Total Exposure
                    <Icon
                        type={exposureRefreshing ? "loading" : "sync"}
                        className={"cc-metric__edit"}
                        onClick={exposureRefreshing ? undefined : onRefreshExposure}
                        style={{
                            marginLeft: 8,
                            cursor: exposureRefreshing ? "not-allowed" : "pointer",
                            fontSize: 12
                        }}
                        title={"Recompute exposure data"}
                    />
                </span>
                <div className={"cc-metric__rows"}>
                    {rows.map(row => {
                        const hasValue =
                            row.amount !== null &&
                            row.amount !== undefined &&
                            row.amount !== "";
                        const isZero = hasValue && Number(row.amount) === 0;
                        return (
                            <div
                                className={
                                    "cc-metric__row" +
                                    (row.highlight
                                        ? ""
                                        : " cc-metric__row--muted")
                                }
                                key={row.key}
                            >
                                <span className={"cc-metric__row-label"}>
                                    {row.label}
                                </span>
                                <span className={"cc-metric__row-value"}>
                                    {!hasValue ? (
                                        "—"
                                    ) : isZero ? (
                                        "N.A."
                                    ) : (
                                        <React.Fragment>
                                            <span
                                                className={"cc-metric__unit"}
                                            >
                                                ₹
                                            </span>
                                            {this.metricDisplay(row)}
                                        </React.Fragment>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // ---- TVR variant -----------------------------------------------------
    // Every block becomes a disabled text field. The value is the FULL amount
    // (no compact toggle here) formatted the same way the tiles show it, so the
    // read-only TVR digest matches the standalone card figure-for-figure.

    // Plain display string for a leaf metric/row (label carries no ₹).
    tvrText(metric) {
        // Editable tile prefers its live value, mirroring renderMetricValue. When
        // no live value is wired (e.g. TVR passes no salesProposedLimit), fall
        // back to metric.value, which already carries the persisted figure.
        const ec = this.editableFor(metric);
        const useLive = ec && ec.value !== null && ec.value !== undefined;
        const amount = useLive ? ec.value : metric.amount;
        // N.A. tiles, and a genuine zero, read as "N.A." — same rule as the tiles.
        const isZero =
            amount !== null &&
            amount !== undefined &&
            amount !== "" &&
            Number(amount) === 0;
        if (metric.na || isZero) {
            return "N.A.";
        }
        if (useLive) {
            return `₹ ${formatCurrency(ec.value, true)}`;
        }
        const hasValue =
            metric.value !== null &&
            metric.value !== undefined &&
            metric.value !== "";
        if (!hasValue) {
            return "—";
        }
        // metric.value is already the full formatted number; prefix ₹.
        return `₹ ${metric.value}`;
    }

    renderTvrField(label, text) {
        return (
            <div className={"cc-tvr-field"} key={label}>
                <div className={"cc-tvr-field__head"}>
                    <span className={"cc-tvr-field__label"}>{label}</span>
                </div>
                <Input className={"cc-tvr-input"} value={text} disabled />
            </div>
        );
    }

    // No CardBlock/heading here — the fields drop straight onto the TVR grid so
    // they read as part of the surrounding Financial Performance form, not a
    // separate card. Nothing to show → render nothing.
    renderTvr() {
        const { topMetrics, exposureData } = this.props;
        const hasTopMetrics = Boolean(topMetrics && topMetrics.length);
        const totalExposure = exposureData && exposureData.total_exposure;
        const hasExposure =
            totalExposure !== null &&
            totalExposure !== undefined &&
            totalExposure !== "";
        if (!hasTopMetrics && !hasExposure) {
            return null;
        }
        return (
            <div className={"cc-tvr-grid"}>
                {hasTopMetrics && topMetrics.map(metric =>
                    metric.rows
                        ? metric.rows.map(row =>
                            this.renderTvrField(
                                `${metric.label} · ${row.label}`,
                                this.tvrText(row)
                            )
                        )
                        : this.renderTvrField(
                            metric.label,
                            this.tvrText(metric)
                        )
                )}
                {hasExposure
                    ? this.renderTvrField(
                        "Total Exposure",
                        `₹ ${formatCurrency(totalExposure, true)}`
                    )
                    : null}
            </div>
        );
    }

    render() {
        if (this.props.variant === "tvr") {
            return this.renderTvr();
        }
        const { topMetrics, title } = this.props;
        const hasMetrics = Boolean(topMetrics && topMetrics.length);
        const hasExposure = Boolean(this.props.onRefreshExposure);
        const { trackerMetric } = this.state;
        // Falls back to {} (not conditionally unmounted) when no tile is open —
        // LimitTrackerModal fetches history / resets its form in
        // componentDidUpdate on the `visible` prop's false→true edge, so it
        // must stay mounted the whole time for that transition to ever fire.
        const trackerMeta = (trackerMetric && LIMIT_META[trackerMetric.key]) || {};
        const trackerEc = this.editableFor(trackerMetric) || {};
        return (
            <CardBlock
                icon={"bar-chart"}
                title={title}
                action={hasMetrics ? this.renderToggle() : null}
            >
                {hasMetrics || hasExposure ? (
                    <div className={"cc-metrics"}>
                        {topMetrics && topMetrics.map(metric =>
                            metric.rows
                                ? this.renderRowedMetric(metric)
                                : (
                                    <div className={"cc-metric"} key={metric.label}>
                                        <span className={"cc-metric__label"}>
                                            {metric.label}
                                        </span>
                                        {this.renderMetricValue(metric)}
                                    </div>
                                )
                        )}
                        {this.renderExposureMetric()}
                    </div>
                ) : (
                    <Empty description={"Summary metrics not available"} />
                )}
                <LimitTrackerModal
                    visible={Boolean(trackerMetric)}
                    caseId={this.props.caseId}
                    limitType={trackerMeta.limitType || ""}
                    title={trackerMeta.title || ""}
                    fieldLabel={trackerMeta.fieldLabel || ""}
                    canEdit={
                        Boolean(trackerMetric) && this.canEditMetric(trackerMetric)
                    }
                    currentValue={trackerEc.value}
                    onSubmit={this.submitTrackedLimit}
                    onClose={this.closeTracker}
                />
            </CardBlock>
        );
    }
}

LimitAssessmentCard.propTypes = {
    // Needed by the limit tracker modals (PATCH/GET history).
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    topMetrics: PropTypes.array,
    // Card heading. Pass null when the host already supplies one.
    title: PropTypes.string,
    // "cards" (interactive tiles) or "tvr" (disabled text fields on the TVR grid).
    variant: PropTypes.oneOf(["cards", "tvr"]),
    proposedLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    salesProposedLimit: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    // Case stage — Sales Proposed Limit is editable only at the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isRiskApprover: PropTypes.bool,
    isUserAllowedToEdit: PropTypes.bool,
    recreditViewOnlyPermission: PropTypes.bool,
    onProposedLimitChange: PropTypes.func,
    onSalesProposedLimitChange: PropTypes.func,
    // Total Exposure box — { group_exposure, proposed_limit, total_exposure,
    // existing_exposure }, plus its own refresh action (build_exposure_data=true).
    exposureData: PropTypes.object,
    exposureRefreshing: PropTypes.bool,
    onRefreshExposure: PropTypes.func
};

LimitAssessmentCard.defaultProps = {
    title: "Limit Assessment",
    variant: "cards"
};

export default LimitAssessmentCard;
